import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Supplier } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplierFiservBuyerFeeRate } from '../../../../../../redux/services/suppliersService';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { openDillToast } from '../../../../../../utils/helpers';
import { DillButton, DillInput } from '../../../../../general/Dill';

interface FiservBuyerFeeRateSettingsSectionProps {
  supplier?: Supplier | null;
}

const DEFAULT_MAX_FEE_RATE = 3.5;

const getInitialRate = (supplier?: Supplier | null): string => {
  const rate = supplier?.fiservBuyerFeeRate;
  if (rate === undefined || rate === null) {
    return '';
  }
  return `${rate}`;
};

export const FiservBuyerFeeRateSettingsSection = ({
  supplier,
}: FiservBuyerFeeRateSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const maxFeeRate = supplier?.creditCardTakeRate ?? DEFAULT_MAX_FEE_RATE;

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [rate, setRate] = useState<string>(() => getInitialRate(supplier));
  const [initialRate, setInitialRate] = useState<string>(() => getInitialRate(supplier));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const newRate = getInitialRate(supplier);
    setRate(newRate);
    setInitialRate(newRate);
  }, [supplier]);

  const error = useMemo(() => {
    const trimmed = rate.trim();
    if (trimmed === '') {
      return 'Enter a fee rate';
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return 'Fee rate must be a number';
    }
    if (parsed < 0) {
      return 'Fee rate cannot be negative';
    }
    if (parsed > maxFeeRate) {
      return `Fee rate cannot be greater than ${maxFeeRate}%`;
    }
    return '';
  }, [rate, maxFeeRate]);

  const rateChanged = rate.trim() !== initialRate.trim();

  const performSave = async () => {
    const parsedRate = Number(rate.trim());
    setSaving(true);
    const result = await dispatch(
      updateSupplierFiservBuyerFeeRate({
        supplierId: supplier?.id || '',
        fiservBuyerFeeRate: parsedRate,
      })
    );
    setSaving(false);

    if (result.type === 'supplier/updateFiservBuyerFeeRate/fulfilled') {
      setInitialRate(`${parsedRate}`);
      setRate(`${parsedRate}`);
      await dispatch(getUserDetails());
      openDillToast({
        type: 'SUCCESS',
        message: 'Card fee rate saved. Customers are being updated in the background.',
      });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save card fee rate' });
    }
  };

  const handleSave = () => {
    if (error || !rateChanged) {
      return;
    }

    dispatch(
      openMessageModal({
        modalType: 'WARN',
        title: 'Update card fee rate for all customers?',
        subTitle: `Saving will set the card fee rate to ${Number(
          rate.trim()
        )}% for all of your customers. Any rate customizations made to specific customers will be overwritten.`,
        buttonText: 'Cancel',
        secondButtonText: 'Proceed',
        isSecondButton: true,
        onSecondButtonClick: async () => {
          await performSave();
        },
      })
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Credit Card Surcharging</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          The percentage fee your customers pay when paying by card. Saving applies this rate to all
          of your customers.
        </p>
      </div>

      <div className="flex flex-col gap-1 flex-1">
        <div className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800">Customer card fee rate (%)</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Maximum allowed is {maxFeeRate}%, your credit card take rate.
            </p>
          </div>
          <div
            className="w-28"
            data-tooltip-id={!canEdit ? 'no-permission-fee-rate-tooltip' : undefined}>
            <DillInput
              value={rate}
              onChange={setRate}
              type="number"
              placeholder="0"
              disabled={!canEdit}
              error={rateChanged ? error : ''}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-fee-rate-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !rateChanged || !!error}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-fee-rate-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
